import { getSession } from '../lib/session'
import { normalizePhone } from '../lib/phone'
import { apiRequest } from '../lib/httpClient'
import { pickNumber, pickString } from './shared/coercion'

export type ProfileDetail = {
  userId: string
  clientId: string
  firstName: string
  lastName: string
  fullName: string
  email: string
  phone: string
  typeId: number
  defaultClientId: string
  defaultPageSize: number
  isTwoFactorLogin: boolean
}

function toRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') return {}
  return value as Record<string, unknown>
}

function mapProfileDetail(payload: unknown): ProfileDetail | null {
  const root = toRecord(payload)
  const detail = toRecord(root['user'] ?? root['User'] ?? payload)
  if (!Object.keys(detail).length) return null

  const firstName = pickString(detail['firstName'] ?? detail['FirstName'])
  const lastName = pickString(detail['lastName'] ?? detail['LastName'])

  return {
    userId: pickString(detail['userId'] ?? detail['UserId']),
    clientId: pickString(detail['clientId'] ?? detail['ClientId']),
    firstName,
    lastName,
    fullName: pickString(detail['fullName'] ?? detail['FullName']) || `${firstName} ${lastName}`.trim(),
    email: pickString(detail['email'] ?? detail['Email']),
    phone: normalizePhone(pickString(detail['phone'] ?? detail['Phone'])),
    typeId: pickNumber(detail['typeId'] ?? detail['TypeId'], 3),
    defaultClientId: pickString(detail['defaultClientId'] ?? detail['DefaultClientId']),
    defaultPageSize: pickNumber(detail['defaultPageSize'] ?? detail['DefaultPageSize'], 10),
    isTwoFactorLogin: Boolean(detail['isTwoFactorLogin'] ?? detail['IsTwoFactorLogin']),
  }
}

export async function fetchProfile(): Promise<ProfileDetail | null> {
  const session = getSession()
  if (!session?.currentUserId) return null

  const payload = await apiRequest<unknown>('user/ui/profile/initialise', {
    method: 'GET',
    query: { userId: session.currentUserId },
  })

  return mapProfileDetail(payload)
}

export async function saveProfile(values: ProfileDetail): Promise<ProfileDetail | null> {
  const payload = await apiRequest<unknown>('user/ui/profile/save', {
    method: 'POST',
    body: {
      ...values,
      fullName: `${values.firstName} ${values.lastName}`.trim(),
      phone: normalizePhone(values.phone),
    },
  })

  return mapProfileDetail(payload) ?? {
    ...values,
    fullName: `${values.firstName} ${values.lastName}`.trim(),
    phone: normalizePhone(values.phone),
  }
}
// POST /api/User/media/avatar — upload the current user's avatar (multipart).
// The avatar is read back via the combined GET /api/User/media (see fetchUserMedia).
export async function uploadAvatar(input: { file: File; isMale: boolean }): Promise<unknown> {
  const formData = new FormData()
  formData.append('file', input.file)
  formData.append('isMale', String(input.isMale))
  return apiRequest<unknown>('user/media/avatar', {
    method: 'POST',
    formData,
  })
}

/** Authenticator-app 2FA for the signed-in user. Only offered while 2FA is turned on for them. */
export type AuthenticatorStatus = {
  available: boolean
  enabled: boolean
  /** An admin reset the app (or the email changed): ask the user to set it up again. */
  setupRequired: boolean
}

export async function fetchAuthenticatorStatus(): Promise<AuthenticatorStatus> {
  return apiRequest<AuthenticatorStatus>('user/ui/mfa/status', { method: 'GET' })
}

export async function startAuthenticatorSetup(password: string) {
  return apiRequest<{ secret: string; otpauthUri: string }>('user/ui/mfa/setup/start', {
    method: 'POST',
    body: { password },
  })
}

export async function confirmAuthenticatorSetup(password: string, code: string) {
  return apiRequest<{ enabled: boolean }>('user/ui/mfa/setup/confirm', {
    method: 'POST',
    body: { password, code },
  })
}

/** Back to email 2FA. Needs the password and a current code from the app. */
export async function disableAuthenticator(password: string, code: string) {
  return apiRequest<{ enabled: boolean }>('user/ui/mfa/disable', {
    method: 'POST',
    body: { password, code },
  })
}

export async function dismissAuthenticatorSetup() {
  return apiRequest<{ success: boolean }>('user/ui/mfa/setup/dismiss', { method: 'POST' })
}
